import React, { useEffect, useRef, useState } from 'react';
import { ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Form, Input, Modal, notification, Space, Tooltip } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import useSWR from 'swr';
import { useAuth } from '../../../context/AuthContext';

function StartLicense(stimulsoftInstance: any) {
    const key = process.env.REACT_APP_STIMULSOFT_LICENSE_KEY;
    if (key && key.trim() !== "") {
        try {
            stimulsoftInstance.Base.StiLicense.key = key.trim();
        } catch (error: any) { }
    } else {
        const hardcodedKey = "6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHmKmIYtQbGVffUafn4grxXukjNz+mLI/d2lO8A9DuHxAxu4MmrN4lqhtklB0Pcnfq+AIk+1s6kU0D6JIIxpfQs1FV6pzwrzAWDLexRPvVgpswR/C5n69gFjFmWmtV0bGCTAj6H/MHgwElWYT9nYccmuFH0sfAj7VELa2HehqREJ7di+05AWeWIScfcWsf0XZ3JK+2X3reYvhUEAxrCwX3FDw2p1hPQIfiUVkUd4NRvfwWBLOjP2ZTfp9dzNe1D6T7miK1VBd731o/ex6q1bkdNH+G1KV6OLxdQwb9zWr4AR0QtMKwrphskgAYb5PMCectoVCeRFKtWSm8qnMP+GN8v0GHbJGQ/ZYWMAsE94nCEhnJz9T/xfy1b6WEWqpUUjVsfPzm/naRz0bxVN+AtFW7thTBMwGXoKTJXnKhfrI5u/fRffUu78ejnazBjWCHANN/xF7XOLhnFWw5JZHPXkaoCbiG8kBL3bgL4DtSyZ7jecAPmJUtainwLNMdo75J6hvu/pS+jDUs2tqRjEv6PTehXg";
        try {
            stimulsoftInstance.Base.StiLicense.key = hardcodedKey;
        } catch (fallbackError: any) { }
    }
}

interface DataType {
    key: React.Key;
    id: number;
    item_code: string;
    item_name: string;
    qty: number;
    qty_supply?: number;
    location: string;
    stock_ready?: number;
}

const ResizableTitle = (props: React.HTMLAttributes<any> & { onResize: (e: React.SyntheticEvent, data: ResizeCallbackData) => void; width: number }) => {
    const { onResize, width, ...restProps } = props;
    if (!width) return <th {...restProps} />;
    return (
        <Resizable width={width} height={0} handle={<span className="react-resizable-handle" onClick={(e) => e.stopPropagation()} />} onResize={onResize} draggableOpts={{ enableUserSelectHack: false }}>
            <th {...restProps} />
        </Resizable>
    );
};

interface WorkOrderRecord {
    work_order_no: string;
    work_order_date: string;
    warehouse: string
    warehouse_name: string;
    no_polisi?: string;
}

interface InsufficientItem {
    item_code: string;
    item_name: string;
    required_qty: number;
    available_qty: number;
}

type DataIndex = keyof DataType;

interface IncomingListItem {
    key: React.Key;
    id: number;
    itemCode: string;
    itemName: string;
    qty: number;
    location: string;
    availableQty?: number;
}

const mapStatusNumberToString = (statusNum: number): string => {
    switch (statusNum) {
        case 1: return 'Draft';
        case 7: return 'Ready';
        case 5: return 'Canceled';
        case 4: return 'Complete';
        default: return String(statusNum);
    }
};

const mapStatusStringToNumber = (statusStr: string): number => {
    switch (statusStr) {
        case 'Draft': return 1;
        case 'Ready': return 7;
        case 'Canceled': return 5;
        case 'Complete': return 4;
        default: return parseInt(statusStr, 10) || 1;
    }
};


const withSupplySlipLogic = (WrappedComponent: React.ComponentType<any>) => {
    const ComponentWithLogic: React.FC = (props) => {
        const [searchText, setSearchText] = useState('');
        const [searchedColumn, setSearchedColumn] = useState('');
        const searchInput = useRef<InputRef>(null);
        const [tableData, setTableData] = useState<DataType[]>([]);
        const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
        const [form] = Form.useForm();
        const [lockedFields, setLockedFields] = useState<Record<string, { userName: string; socketId: string }>>({});
        const lockedFieldsRef = useRef(lockedFields);
        lockedFieldsRef.current = lockedFields;
        const { socket, userData } = useAuth();
        const currentStatus = Form.useWatch('status', form);
        const currentWarehouse = Form.useWatch('warehouse', form);
        const itemCodes = tableData.map(item => item.item_code);
        const { data: realtimeStocks, mutate: mutateStocks } = useSWR(
            currentWarehouse && itemCodes.length > 0
                ? `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/stock?warehouse=${currentWarehouse}&item_codes=${JSON.stringify(itemCodes)}`
                : null,
            async (url) => {
                const res = await axios.get(url);
                return res.data;
            },
            { revalidateOnFocus: false, revalidateOnReconnect: true }
        );
        useEffect(() => {
            if (!socket) return;
            const handleStockUpdated = () => {
                mutateStocks();
            };
            socket.on("stock_updated", handleStockUpdated);
            return () => {
                socket.off("stock_updated", handleStockUpdated);
            };
        }, [socket, mutateStocks]);
        useEffect(() => {
            if (realtimeStocks && Array.isArray(realtimeStocks)) {
                setTableData(prev => {
                    const stockMap = new Map<string, number>(
                        realtimeStocks.map((s: any) => [s.item_code, s.qty])
                    );
                    let changed = false;
                    const nextData = prev.map(item => {
                        const stock = stockMap.get(item.item_code) ?? 0;
                        let updatedQtySupply = item.qty_supply ?? item.qty;
                        const statusNum = mapStatusStringToNumber(currentStatus);
                        if (statusNum !== 4 && updatedQtySupply > stock) {
                            updatedQtySupply = stock;
                        }
                        if (item.stock_ready !== stock || item.qty_supply !== updatedQtySupply) {
                            changed = true;
                            return {
                                ...item,
                                stock_ready: stock,
                                qty_supply: updatedQtySupply
                            };
                        }
                        return item;
                    });
                    return changed ? nextData : prev;
                });
            }
        }, [realtimeStocks, currentStatus])
        const [isWorkOrderModalVisible, setIsWorkOrderModalVisible] = useState(false);
        const [isImportingWorkOrder, setIsImportingWorkOrder] = useState(false);
        const [currentWorkOrderNoForSSN, setCurrentWorkOrderNoForSSN] = useState<string | null>(null);
        const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
        const [itemsForConfirmModal, setItemsForConfirmModal] = useState<InsufficientItem[]>([]);
        const [recordForConfirmModal, setRecordForConfirmModal] = useState<WorkOrderRecord | null>(null);
        const [pendingImportedItems, setPendingImportedItems] = useState<DataType[]>([]);
        const [isListItemWorkOrderModalVisible, setIsListItemWorkOrderModalVisible] = useState(false);
        const [workOrderNoForListItemModal, setWorkOrderNoForListItemModal] = useState<string | null>(null);
        const [existingItemCodesForListItemModal, setExistingItemCodesForListItemModal] = useState<string[]>([]);
        const [warehouseForListItemModal, setWarehouseForListItemModal] = useState<string | null>(null);
        const [autosaveStatus, setAutosaveStatus] = useState<'stopped' | 'processing' | 'error' | 'success'>('stopped');
        const [isSupplySlipSaved, setIsSupplySlipSaved] = useState(false);
        const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
        const prevTableDataRef = useRef<DataType[]>([]);
        const [displayAutosaveStatus, setDisplayAutosaveStatus] = useState<'stopped' | 'processing' | 'error' | 'success'>('stopped');
        const processingStartTimeRef = useRef<number | null>(null);
        const statusDisplayTimerRef = useRef<NodeJS.Timeout | null>(null);
        const hasInitialSaveTriggeredRef = useRef(false);
        const isSavingRef = useRef(false);
        const [isPrinting, setIsPrinting] = useState(false);
        const [isReportModalOpen, setIsReportModalOpen] = useState(false)
        const location = useLocation();
        const navigate = useNavigate();
        const queryParams = new URLSearchParams(location.search);
        const urlSupplySlipNo = queryParams.get('supplySlipNo');
        const [isEditingExistingSupplySlip, setIsEditingExistingSupplySlip] = useState(false);
        const urlStatusLabel = queryParams.get('statusLabel');
        const isOverSupply = tableData.some(item => (item.qty_supply ?? item.qty) > item.qty);
        const isVoidButtonDisabled = currentStatus !== 'Draft';
        const isSubmitButtonDisabled = currentStatus !== 'Draft' || isOverSupply || autosaveStatus === 'processing';
        const isPrintButtonDisabled = currentStatus === 'Draft';
        const currentSupplySlipNo = Form.useWatch('supply_slip_no', form) || urlSupplySlipNo;

        useEffect(() => {
            if (!socket || !currentSupplySlipNo) return;
            socket.emit("join_supply_slip_room", { supplySlipNo: currentSupplySlipNo });

            const handleCurrentLocks = (locks: Record<string, { userName: string; socketId: string }>) => {
                setLockedFields(locks);
            };
            const handleFieldLocked = (data: { fieldName: string; userName: string; socketId: string }) => {
                setLockedFields((prev) => ({ ...prev, [data.fieldName]: { userName: data.userName, socketId: data.socketId } }));
            };
            const handleFieldUnlocked = (data: { fieldName: string }) => {
                setLockedFields((prev) => {
                    const updated = { ...prev };
                    delete updated[data.fieldName];
                    return updated;
                });
            };
            const handleReceiveSyncData = (data: { changedValues?: any; allValues?: any; tableItems?: DataType[] }) => {
                if (data.changedValues) {
                    form.setFieldsValue(data.changedValues);
                }
                if (data.allValues) {
                    form.setFieldsValue(data.allValues);
                }
                if (data.tableItems) {
                    setTableData(data.tableItems);
                }
            };

            const handleStatusUpdated = (data: { action: string; supplySlipNo: string }) => {
                if (data.action === "voided") {
                    Modal.warning({
                        title: "Dokumen Dibatalkan (Void)",
                        content: `Dokumen Supply Slip ${data.supplySlipNo} telah dibatalkan/dihapus oleh pengguna lain. Data form dan tabel akan direset.`,
                        onOk: () => {
                            handleNew();
                        }
                    });
                } else if (data.action === "submitted") {
                    Modal.info({
                        title: "Dokumen Telah Disubmit",
                        content: `Dokumen Supply Slip ${data.supplySlipNo} telah berhasil disubmit oleh pengguna lain. Form sekarang berstatus Complete.`,
                        onOk: () => {
                            form.setFieldsValue({ status: "Complete" });
                            setAutosaveStatus('stopped');
                        }
                    });
                }
            };

            socket.on("supply_slip_current_locks", handleCurrentLocks);
            socket.on("supply_slip_field_locked", handleFieldLocked);
            socket.on("supply_slip_field_unlocked", handleFieldUnlocked);
            socket.on("supply_slip_receive_sync_data", handleReceiveSyncData);
            socket.on("supply_slip_status_updated", handleStatusUpdated);

            return () => {
                socket.emit("leave_supply_slip_room", { supplySlipNo: currentSupplySlipNo });
                socket.off("supply_slip_current_locks", handleCurrentLocks);
                socket.off("supply_slip_field_locked", handleFieldLocked);
                socket.off("supply_slip_field_unlocked", handleFieldUnlocked);
                socket.off("supply_slip_receive_sync_data", handleReceiveSyncData);
                socket.off("supply_slip_status_updated", handleStatusUpdated);
            };
        }, [socket, currentSupplySlipNo, form]);

        const onFieldFocus = (fieldName: string) => {
            if (socket && currentSupplySlipNo) {
                socket.emit("supply_slip_lock_field", {
                    supplySlipNo: currentSupplySlipNo,
                    fieldName,
                    userName: userData?.nama || "User Lain"
                });
            }
        };

        const onFieldBlur = (fieldName: string) => {
            if (socket && currentSupplySlipNo) {
                socket.emit("supply_slip_unlock_field", {
                    supplySlipNo: currentSupplySlipNo,
                    fieldName
                });
            }
        };

        const onFormValuesChange = (changedValues: any, allValues: any) => {
            if (socket && currentSupplySlipNo) {
                socket.emit("supply_slip_sync_field_data", {
                    supplySlipNo: currentSupplySlipNo,
                    changedValues,
                    allValues,
                    tableItems: tableData
                });
            }
        };

        const getLockProps = (fieldName: string) => ({
            help: lockedFields?.[fieldName] ? `Sedang diedit oleh ${lockedFields[fieldName].userName}` : undefined,
            validateStatus: (lockedFields?.[fieldName] ? "warning" : undefined) as "warning" | undefined,
        });

        const handleNew = () => {
            form.resetFields();
            form.setFieldsValue({ status: "Draft" });
            form.setFieldsValue({ supply_slip_date: moment().format('DD/MM/YYYY') });
            setTableData([]);
            setIsSupplySlipSaved(false);
            setCurrentWorkOrderNoForSSN(null);
            setIsEditingExistingSupplySlip(false);
            setAutosaveStatus('stopped');
            hasInitialSaveTriggeredRef.current = false
            navigate('/dashboard/supply_slip');
        };

        const handleSearch = (
            selectedKeys: string[],
            confirm: FilterDropdownProps['confirm'],
            dataIndex: DataIndex,
        ) => {
            confirm();
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
        };

        const handleReset = (clearFilters: () => void) => {
            clearFilters();
            setSearchText('');
        };

        const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        ref={searchInput}
                        placeholder={`Search ${dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => clearFilters && handleReset(clearFilters)}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                close();
                            }}
                        >
                            close
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
            ),
            onFilter: (value, record) =>
                (record[dataIndex] || '')
                    .toString()
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase()),
            onFilterDropdownOpenChange: (visible) => {
                if (visible) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
            render: (text) =>
                searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                ),
        });

        const [columns, setColumns] = useState<TableColumnsType<DataType>>([
            { title: 'No', render: (text, record, index) => index + 1, key: 'no', width: 50, ellipsis: true },
            { title: 'Item Code', dataIndex: 'item_code', key: 'item_code', width: 150, ...getColumnSearchProps('item_code'), sorter: (a, b) => a.item_code.localeCompare(b.item_code), sortDirections: ['descend', 'ascend'], ellipsis: true },
            { title: 'Item Name', dataIndex: 'item_name', key: 'item_name', width: 250, ...getColumnSearchProps('item_name'), sorter: (a, b) => a.item_name.localeCompare(b.item_name), sortDirections: ['descend', 'ascend'], ellipsis: true },
            { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 80, ...getColumnSearchProps('qty'), sorter: (a, b) => a.qty - b.qty, sortDirections: ['descend', 'ascend'], ellipsis: true },
            {
                title: 'Qty Supply', dataIndex: 'qty_supply', key: 'qty_supply', width: 100, render: (text: any, record: DataType) => {
                    const lockInfo = lockedFieldsRef.current?.[`qty_supply_${record.key}`];
                    const isLockedByOther = lockInfo && lockInfo.socketId !== socket?.id;
                    return (
                        <Tooltip title={isLockedByOther ? `Sedang diedit oleh ${lockInfo.userName}` : undefined}>
                            <div>
                                <Input
                                    type="number"
                                    disabled={!!isLockedByOther}
                                    onFocus={() => onFieldFocus(`qty_supply_${record.key}`)}
                                    onBlur={() => onFieldBlur(`qty_supply_${record.key}`)}
                                    value={record.qty_supply ?? record.qty}
                                    onChange={(e) => {
                                        if (isLockedByOther) return;
                                        let val = parseInt(e.target.value, 10) || 0;
                                        const maxStock = record.stock_ready ?? 0;
                                        const statusNum = mapStatusStringToNumber(form.getFieldValue('status'));
                                        if (statusNum !== 4 && val > maxStock) {
                                            val = maxStock;
                                        }
                                        setTableData((prev) => {
                                            const newTable = prev.map((row) => (row.key === record.key ? { ...row, qty_supply: val } : row));
                                            if (socket && currentSupplySlipNo) {
                                                socket.emit("supply_slip_sync_field_data", {
                                                    supplySlipNo: currentSupplySlipNo,
                                                    allValues: form.getFieldsValue(true),
                                                    tableItems: newTable
                                                });
                                            }
                                            return newTable;
                                        });
                                    }}
                                    status={(record.qty_supply ?? record.qty) > record.qty ? "error" : (isLockedByOther ? "warning" : "")}
                                />
                                {(record.qty_supply ?? record.qty) > record.qty && (
                                    <div style={{ color: 'red', fontSize: '12px', marginTop: '4px', fontWeight: 'bold' }}>Over Supply !</div>
                                )}
                            </div>
                        </Tooltip>
                    );
                },
                ellipsis: true
            },
            {
                title: 'Stock Ready',
                dataIndex: 'stock_ready',
                key: 'stock_ready',
                width: 100,
                render: (text: any, record: DataType) => record.stock_ready ?? 0,
                ellipsis: true
            },
            { title: 'Location', dataIndex: 'location', key: 'location', width: 120, ...getColumnSearchProps('location'), sorter: (a, b) => a.location.localeCompare(b.location), sortDirections: ['descend', 'ascend'], ellipsis: true },
        ]);

        const handleResize = (index: number) => (e: React.SyntheticEvent, { size }: ResizeCallbackData) => {
            setColumns((prevColumns) => {
                const newColumns = [...prevColumns];
                newColumns[index] = { ...newColumns[index], width: size.width };
                return newColumns;
            });
        };

        const resizableColumns = columns.map((col, index) => ({
            ...col,
            onHeaderCell: (column: any) => ({
                width: column.width,
                onResize: handleResize(index),
            }),
        }));

        const components = { header: { cell: ResizableTitle } };

        const showWorkOrderModal = () => {
            setIsWorkOrderModalVisible(true);
        };

        const handleWorkOrderModalCancel = () => {
            setIsWorkOrderModalVisible(false);
        };

        const saveSupplySlipData = async (isUpdate: boolean) => {
            if (isSavingRef.current) return;
            isSavingRef.current = true;
            setAutosaveStatus('processing');
            try {
                const formValues = form.getFieldsValue();
                const statusToSave = mapStatusStringToNumber(formValues.status);
                const supplySlipHeader = {
                    status: statusToSave,
                    supply_slip_no: formValues.supply_slip_no,
                    supply_slip_date: formValues.supply_slip_date,
                    work_order_no: formValues.work_order_no,
                    work_order_date: formValues.work_order_date,
                    no_polisi: formValues.no_polisi,
                    warehouse: formValues.warehouse,
                };

                const supplySlipItems = tableData.map(item => ({
                    item_code: item.item_code,
                    item_name: item.item_name,
                    qty: item.qty_supply ?? item.qty,
                    location: item.location,
                    work_order_no: formValues.work_order_no,
                    supply_slip_no: formValues.supply_slip_no,
                }));

                if (!supplySlipHeader.supply_slip_no || !supplySlipHeader.work_order_no) {
                    console.warn("Cannot save: Supply Slip No or Work Order No is missing.");
                    setAutosaveStatus('error');
                    return;
                }

                let response;
                if (isUpdate) {
                    response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/${supplySlipHeader.supply_slip_no}`, {
                        header: supplySlipHeader,
                        items: supplySlipItems,
                    });
                } else {
                    response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip`, {
                        header: supplySlipHeader,
                        items: supplySlipItems,
                    });
                }

                if (response.status === 200 || response.status === 201) {
                    setAutosaveStatus('success');
                    setIsSupplySlipSaved(true);
                    prevTableDataRef.current = tableData;
                } else {
                    setAutosaveStatus('error');
                }
            } catch (error) {
                console.error("Autosave failed:", error);
                setAutosaveStatus('error');
            } finally {
                isSavingRef.current = false;
            }
        };

        const handleSubmit = async () => {
            const supplySlipNo = form.getFieldValue('supply_slip_no');
            const currentStatus = form.getFieldValue('status');
            const isOverSupply = tableData.some(item => (item.qty_supply ?? item.qty) > item.qty);

            if (isOverSupply) {
                notification.error({
                    message: 'Peringatan',
                    description: 'Over Supply !, Harap Perhatikan !',
                });
                return;
            }

            if (!supplySlipNo) {
                Modal.warning({
                    title: 'Peringatan',
                    content: 'Nomor Supply Slip belum tersedia. Silakan pilih Work Order terlebih dahulu atau simpan draft.',
                });
                return;
            }

            if (currentStatus === 'Complete') {
                Modal.info({
                    title: 'Informasi',
                    content: 'Supply Slip ini sudah berstatus Complete.',
                });
                return;
            }

            Modal.confirm({
                title: 'Konfirmasi Submit',
                icon: <ExclamationCircleOutlined />,
                content: 'Apakah Anda yakin ingin Submit Supply Slip ini? Tindakan ini akan mengubah status menjadi Complete dan memotong stok.',
                async onOk() {
                    setAutosaveStatus('processing');
                    try {
                        const response = await axios.post(
                            `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/submit/${supplySlipNo}`
                        );

                        if (response.status === 200) {
                            form.setFieldsValue({ status: 'Complete' });
                            setAutosaveStatus('stopped');
                            const isQtyModified = response.data?.isQtyModified;
                            if (isQtyModified) {
                                Modal.success({
                                    title: 'Berhasil',
                                    content: 'Supply Slip berhasil',
                                });
                            }
                        } else {
                            setAutosaveStatus('error');
                            Modal.error({
                                title: 'Gagal',
                                content: 'Gagal submit Supply Slip. Silakan coba lagi.',
                            });
                        }
                    } catch (error) {
                        console.error("Error submitting Supply Slip:", error);
                        setAutosaveStatus('error');
                        Modal.error({
                            title: 'Error',
                            content: 'Terjadi kesalahan saat submit Supply Slip. Hub Dev untuk detail.',
                        });
                    }
                },
                onCancel() {
                    console.log('Submit dibatalkan.');
                },
            });
        };

        const handleVoid = async () => {
            const supplySlipNo = form.getFieldValue('supply_slip_no');

            if (!supplySlipNo) {
                Modal.warning({
                    title: 'Peringatan',
                    content: 'Tidak ada Supply Slip yang dipilih untuk dibatalkan.',
                });
                return;
            }
            Modal.confirm({
                title: 'Konfirmasi Pembatalan Supply Slip',
                icon: <ExclamationCircleOutlined />,
                content: `Apakah Anda yakin ingin membatalkan Supply Slip No: ${supplySlipNo}? Tindakan ini akan menghapus data Supply Slip dan item-itemnya secara permanen.`,
                async onOk() {
                    setAutosaveStatus('processing');
                    try {
                        const response = await axios.delete(
                            `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/${supplySlipNo}`
                        );

                        if (response.status === 200) {
                            form.resetFields();
                            form.setFieldsValue({ status: "Draft" });
                            const currentDate = moment().format('DD/MM/YYYY');
                            form.setFieldsValue({ supply_slip_date: currentDate });
                            setTableData([]);
                            setSelectedRowKeys([]);
                            setIsSupplySlipSaved(false);
                            setCurrentWorkOrderNoForSSN(null);
                            setIsEditingExistingSupplySlip(false);
                            hasInitialSaveTriggeredRef.current = false;
                            setAutosaveStatus('stopped');
                            navigate('/dashboard/supply_slip');
                            Modal.success({
                                title: 'Berhasil',
                                content: `Supply Slip No: ${supplySlipNo} berhasil dibatalkan.`,
                            });
                        } else {
                            setAutosaveStatus('error');
                            Modal.error({
                                title: 'Gagal',
                                content: 'Gagal membatalkan Supply Slip. Silakan coba lagi.',
                            });
                        }
                    } catch (error) {
                        console.error("Error voiding Supply Slip:", error);
                        setAutosaveStatus('error');
                        Modal.error({
                            title: 'Error',
                            content: 'Terjadi kesalahan saat membatalkan Supply Slip. Periksa konsol untuk detail.',
                        });
                    }
                },
                onCancel() {
                    console.log('Pembatalan dibatalkan oleh pengguna.');
                },
            });
        };

        const handleConfirmProceed = async () => {
            if (pendingImportedItems.length > 0) {
                setTableData(pendingImportedItems);
                handleWorkOrderModalCancel();
            }
            setIsConfirmModalVisible(false);
            setItemsForConfirmModal([]);
            setRecordForConfirmModal(null);
            setPendingImportedItems([]);
        };

        const handleConfirmCancel = () => {
            console.log('Proses pemilihan Work Order dibatalkan oleh pengguna.');
            setIsConfirmModalVisible(false);
            setItemsForConfirmModal([]);
            setRecordForConfirmModal(null);
        };

        const showListItemWorkOrderModal = () => {
            const currentWorkOrderNo = form.getFieldValue('work_order_no');
            const currentWarehouse = form.getFieldValue('warehouse');
            if (!currentWorkOrderNo) {
                Modal.warning({
                    title: 'Peringatan',
                    content: 'Silakan pilih Work Order terlebih dahulu.',
                });
                return;
            }
            if (lockedFields?.["add_item_modal"]) {
                notification.warning({
                    message: "Modal Sedang Digunakan",
                    description: `Modal Add Item sedang dibuka oleh ${lockedFields["add_item_modal"].userName}`
                });
                return;
            }
            onFieldFocus("add_item_modal");
            setWorkOrderNoForListItemModal(currentWorkOrderNo);
            setWarehouseForListItemModal(currentWarehouse);
            const currentItemCodes = tableData.map(item => item.item_code);
            setExistingItemCodesForListItemModal(currentItemCodes);
            setIsListItemWorkOrderModalVisible(true);
        };

        const handleListItemWorkOrderModalCancel = () => {
            onFieldBlur("add_item_modal");
            setIsListItemWorkOrderModalVisible(false);
            setWorkOrderNoForListItemModal(null);
            setExistingItemCodesForListItemModal([]);
            setWarehouseForListItemModal(null);
        };

        const handleListItemWorkOrderAdd = (newItems: IncomingListItem[]) => {
            const itemsToAdd = newItems.map(item => {
                const available = item.availableQty !== undefined ? item.availableQty : item.qty;
                const qtySupply = Math.min(item.qty, available);
                return {
                    key: item.key || Date.now() + Math.random(),
                    id: item.id,
                    item_code: item.itemCode,
                    item_name: item.itemName,
                    qty: item.qty,
                    qty_supply: qtySupply,
                    location: item.location,
                };
            });
            setTableData((prevData) => {
                const newDataSource = [...prevData, ...itemsToAdd];
                if (socket && currentSupplySlipNo) {
                    socket.emit("supply_slip_sync_field_data", {
                        supplySlipNo: currentSupplySlipNo,
                        allValues: form.getFieldsValue(true),
                        tableItems: newDataSource
                    });
                }
                return newDataSource;
            });
            onFieldBlur("add_item_modal");
            setIsListItemWorkOrderModalVisible(false);
            setSelectedRowKeys([]);
        };

        const handleWorkOrderSelect = async (record: WorkOrderRecord) => {
            setIsImportingWorkOrder(true);
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/work-orders/${record.work_order_no}/prepare-import?warehouse=${record.warehouse}`
                );
                const data = res.data;
                if (data.status === 'DRAFT_EXISTS') {
                    Modal.warning({ title: 'Peringatan', content: `Nomor ${record.work_order_no} sudah dalam keadaan draft di supply slip harap selesaikan !` });
                    return;
                }
                if (data.status === 'NOT_ALLOCATED') {
                    Modal.warning({ title: 'Peringatan', content: 'Work Order belum di alokasi mekanik ! hubungi tim SA' });
                    return;
                }
                if (data.status === 'FULFILLED') {
                    notification.warning({ message: 'Informasi', description: 'Work Order telah terpenuhi semua !' });
                    return;
                }
                form.setFieldsValue({
                    work_order_no: record.work_order_no,
                    work_order_date: record.work_order_date,
                    warehouse: record.warehouse,
                    warehouse_name_display: record.warehouse_name,
                    no_polisi: data.no_polisi || record.no_polisi || '',
                });
                if (data.insufficientItems && data.insufficientItems.length > 0) {
                    setIsConfirmModalVisible(true);
                    setItemsForConfirmModal(data.insufficientItems);
                    setPendingImportedItems(data.items || []);
                } else {
                    setTableData(data.items || []);
                    handleWorkOrderModalCancel();
                }
            } catch (error) {
                console.error("Gagal melakukan pengecekan stok atau mengambil detail Work Order:", error);

                Modal.error({
                    title: 'Terjadi Kesalahan',
                    content: 'Gagal melakukan pengecekan stok atau mengambil detail Work Order. Silakan coba lagi.',
                });
            } finally {
                setIsImportingWorkOrder(false);
            }

        };


        useEffect(() => {
            if (urlSupplySlipNo) {
                setIsEditingExistingSupplySlip(true);
                hasInitialSaveTriggeredRef.current = true;
                const fetchExistingSupplySlip = async () => {
                    setAutosaveStatus('processing');
                    try {
                        const headerResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/${urlSupplySlipNo}`);
                        const headerData = headerResponse.data;

                        const itemsResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/${urlSupplySlipNo}/items`);
                        const itemData = itemsResponse.data.map((item: any) => ({
                            ...item,
                            key: item.id,
                            qty_supply: item.qty_supply,
                            qty: item.qty,
                        }));

                        form.setFieldsValue({
                            status: urlStatusLabel || mapStatusNumberToString(headerData.status),
                            supply_slip_no: headerData.supply_slip_no,
                            supply_slip_date: headerData.supply_slip_date,
                            work_order_no: headerData.work_order_no,
                            work_order_date: headerData.work_order_date,
                            no_polisi: headerData.no_polisi,
                            warehouse: headerData.warehouse,
                            warehouse_name_display: headerData.warehouse_name,
                        });
                        setTableData(itemData);
                        prevTableDataRef.current = itemData;
                        setIsSupplySlipSaved(true);
                        setCurrentWorkOrderNoForSSN(headerData.work_order_no);
                        setAutosaveStatus('success');
                    } catch (error) {
                        console.error("Error fetching existing Supply Slip data:", error);
                        setAutosaveStatus('error');
                        Modal.error({
                            title: 'Error',
                            content: 'Gagal memuat data Supply Slip yang ada. Silakan coba lagi.',
                        });
                    }
                };
                fetchExistingSupplySlip();
            } else {
                setIsEditingExistingSupplySlip(false);
                hasInitialSaveTriggeredRef.current = false;
                form.resetFields();
                form.setFieldsValue({ status: "Draft" });
                const currentDate = moment().format('DD/MM/YYYY');
                form.setFieldsValue({ supply_slip_date: currentDate });
                setTableData([]);
                setIsSupplySlipSaved(false);
                setCurrentWorkOrderNoForSSN(null);
                setAutosaveStatus('stopped');
            }
        }, [urlSupplySlipNo, form, urlStatusLabel]);

        useEffect(() => {
            const workOrderNo = form.getFieldValue('work_order_no');
            if (!isEditingExistingSupplySlip && workOrderNo && workOrderNo !== currentWorkOrderNoForSSN) {
                const generateAndSetSupplySlipNo = async () => {
                    try {
                        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/generate-no`);
                        const newSupplySlipNo = response.data.supply_slip_no;
                        form.setFieldsValue({ supply_slip_no: newSupplySlipNo });
                        setCurrentWorkOrderNoForSSN(workOrderNo);

                    } catch (error) {
                        console.error("Gagal menghasilkan Supply Slip No:", error);
                        setAutosaveStatus('error');
                    }
                };
                generateAndSetSupplySlipNo();
            } else if (!isEditingExistingSupplySlip && !workOrderNo && currentWorkOrderNoForSSN) {
                form.setFieldsValue({ supply_slip_no: undefined });
                setCurrentWorkOrderNoForSSN(null);
                setTableData([]);
                setIsSupplySlipSaved(false);
                setAutosaveStatus('stopped');
                hasInitialSaveTriggeredRef.current = false;
            }
        }, [form, form.getFieldValue('work_order_no'), currentWorkOrderNoForSSN, isEditingExistingSupplySlip]);

        useEffect(() => {
            const currentSupplySlipNo = form.getFieldValue('supply_slip_no');
            const currentWorkOrderNo = form.getFieldValue('work_order_no');
            if (!isEditingExistingSupplySlip && currentSupplySlipNo && currentWorkOrderNo && !isSupplySlipSaved && tableData.length > 0) {
                if (!hasInitialSaveTriggeredRef.current) {
                    hasInitialSaveTriggeredRef.current = true;
                    console.log("Triggering initial save of Supply Slip header and items...");
                    saveSupplySlipData(false);
                }
            }
        }, [form.getFieldValue('supply_slip_no'), form.getFieldValue('work_order_no'), tableData, isSupplySlipSaved, isEditingExistingSupplySlip])

        useEffect(() => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            const currentStatusString = form.getFieldValue('status');
            const currentStatusNumber = mapStatusStringToNumber(currentStatusString);

            if (currentStatusNumber !== 1) {
                console.log("Autosave update stopped: Supply Slip is not in Draft status.");
                setAutosaveStatus('stopped');
                return;
            }

            if (isSupplySlipSaved && JSON.stringify(tableData) !== JSON.stringify(prevTableDataRef.current)) {
                debounceTimerRef.current = setTimeout(() => {
                    console.log("Table data changed, triggering autosave update...");
                    saveSupplySlipData(true);
                }, 1000);
            }

            return () => {
                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                }
            };
        }, [tableData, isSupplySlipSaved, form.getFieldValue('status')]);

        useEffect(() => {

            if (statusDisplayTimerRef.current) {
                clearTimeout(statusDisplayTimerRef.current);
            }

            if (autosaveStatus === 'processing') {
                processingStartTimeRef.current = Date.now();
                setDisplayAutosaveStatus('processing');
            } else if (autosaveStatus === 'success' || autosaveStatus === 'error') {
                const minimumDisplayTime = 800;
                const elapsedTime = processingStartTimeRef.current ? Date.now() - processingStartTimeRef.current : 0;

                if (elapsedTime < minimumDisplayTime) {
                    const remainingTime = minimumDisplayTime - elapsedTime;
                    statusDisplayTimerRef.current = setTimeout(() => {
                        setDisplayAutosaveStatus(autosaveStatus);
                        processingStartTimeRef.current = null;
                    }, remainingTime);
                } else {
                    setDisplayAutosaveStatus(autosaveStatus);
                    processingStartTimeRef.current = null;
                }
            } else {
                setDisplayAutosaveStatus('stopped');
                processingStartTimeRef.current = null;
            }

            return () => {
                if (statusDisplayTimerRef.current) {
                    clearTimeout(statusDisplayTimerRef.current);
                }
            };
        }, [autosaveStatus]);

        const handlePrintClick = async () => {
            const supplySlipNo = form.getFieldValue('supply_slip_no');
            if (!supplySlipNo) {
                notification.warning({ message: "Nomor Supply Slip tidak ditemukan untuk dicetak." });
                return;
            }
            setIsPrinting(true);
            try {
                const headerResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/${encodeURIComponent(supplySlipNo)}`);
                const headerData = headerResponse.data;
                if (!headerData) {
                    notification.error({ message: "Data header Supply Slip tidak ditemukan." });
                    return;
                }
                const itemsResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/${encodeURIComponent(supplySlipNo)}/items`);
                const itemsData = itemsResponse.data;
                const workOrderNo = headerData.work_order_no || form.getFieldValue('work_order_no');
                let companyName = '-';
                if (workOrderNo) {
                    try {
                        const woResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/work-orders/${encodeURIComponent(workOrderNo)}`);
                        companyName = woResponse.data.bill_to_name || '-';
                    } catch (err) {
                        console.error("Failed to fetch company name from work order:", err);
                    }
                }
                const reportPayload = {
                    Header: [{
                        supply_slip_no: headerData.supply_slip_no || "",
                        supply_slip_date: headerData.supply_slip_date || "",
                        work_order_no: headerData.work_order_no || "",
                        work_order_date: headerData.work_order_date || "",
                        no_polisi: headerData.no_polisi || "",
                        warehouse_name: headerData.warehouse_name || "",
                        company_name: companyName,
                    }],
                    Details: itemsData.map((item: any, index: number) => ({
                        no: index + 1,
                        item_code: item.item_code || "",
                        item_name: item.item_name || "",
                        qty: parseFloat(item.qty_supply ?? item.qty ?? 0),
                        location: item.location || "",
                        remark: item.remark || "",
                    }))
                };
                await import("stimulsoft-reports-js/Css/stimulsoft.viewer.office2013.whiteblue.css");
                await import("stimulsoft-reports-js/Scripts/stimulsoft.reports");
                const viewerModule = await import("stimulsoft-reports-js/Scripts/stimulsoft.viewer");
                const Stimulsoft = viewerModule.Stimulsoft || (window as any).Stimulsoft;
                StartLicense(Stimulsoft);
                setIsReportModalOpen(true);
                setTimeout(() => {
                    const report = Stimulsoft.Report.StiReport.createNewReport();
                    report.loadFile("/reports/supply_slip/supply_slip.mrt");
                    const dataSet = new Stimulsoft.System.Data.DataSet("ReportData");
                    dataSet.readJson(reportPayload);
                    report.regData(dataSet.dataSetName, "", dataSet);
                    report.dictionary.synchronize();
                    const options = new Stimulsoft.Viewer.StiViewerOptions();
                    options.toolbar.showOpenButton = true;
                    const viewer = new Stimulsoft.Viewer.StiViewer(options, "StiViewer", false);
                    (viewer as any).onOpenReport = (args: any) => {
                        const newReport = args.report;
                        if (newReport) {
                            newReport.regData(dataSet.dataSetName, "", dataSet);
                            newReport.dictionary.synchronize();
                            viewer.report = newReport;
                        }
                    };
                    viewer.report = report;
                    viewer.renderHtml("sti-viewer-container");
                }, 300);
            } catch (error) {
                console.error("Gagal memuat Stimulsoft Report:", error);
                notification.error({ message: "Gagal memuat preview laporan Stimulsoft." });
            } finally {
                setIsPrinting(false);
            }
        };

        const handleReportModalCancel = () => {
            setIsReportModalOpen(false);
        };

        const handleDelete = () => {
            const keysToDelete = [...selectedRowKeys];
            if (socket && currentSupplySlipNo) {
                keysToDelete.forEach((key) => {
                    socket.emit("supply_slip_unlock_field", {
                        supplySlipNo: currentSupplySlipNo,
                        fieldName: `row_${key}`,
                    });
                });
            }
            setTableData((prevData) => {
                const newData = prevData.filter((item) => !keysToDelete.includes(item.key));
                if (socket && currentSupplySlipNo) {
                    socket.emit("supply_slip_sync_field_data", {
                        supplySlipNo: currentSupplySlipNo,
                        allValues: form.getFieldsValue(true),
                        tableItems: newData,
                    });
                }
                return newData;
            });
            setSelectedRowKeys([]);
        };

        const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
            const addedKeys = newSelectedRowKeys.filter((k) => !selectedRowKeys.includes(k));
            const removedKeys = selectedRowKeys.filter((k) => !newSelectedRowKeys.includes(k));
            if (socket && currentSupplySlipNo) {
                addedKeys.forEach((key) => {
                    socket.emit("supply_slip_lock_field", {
                        supplySlipNo: currentSupplySlipNo,
                        fieldName: `row_${key}`,
                        userName: userData?.nama || "User Lain",
                    });
                });
                removedKeys.forEach((key) => {
                    socket.emit("supply_slip_unlock_field", {
                        supplySlipNo: currentSupplySlipNo,
                        fieldName: `row_${key}`,
                    });
                });
            }
            setSelectedRowKeys(newSelectedRowKeys);
        };

        const rowSelection = {
            selectedRowKeys,
            onChange: onSelectChange,
        };

        return <WrappedComponent
            {...props}
            form={form}
            columns={resizableColumns}
            components={components}
            data={tableData}
            rowSelection={rowSelection}
            handleDelete={handleDelete}
            selectedRowKeys={selectedRowKeys}
            isWorkOrderModalVisible={isWorkOrderModalVisible}
            isImportingWorkOrder={isImportingWorkOrder}
            showWorkOrderModal={showWorkOrderModal}
            handleWorkOrderModalCancel={handleWorkOrderModalCancel}
            handleWorkOrderSelect={handleWorkOrderSelect}
            pagination={{ pageSize: 10 }}
            isConfirmModalVisible={isConfirmModalVisible}
            itemsForConfirmModal={itemsForConfirmModal}
            handleConfirmProceed={handleConfirmProceed}
            handleConfirmCancel={handleConfirmCancel}
            isListItemWorkOrderModalVisible={isListItemWorkOrderModalVisible}
            showListItemWorkOrderModal={showListItemWorkOrderModal}
            handleListItemWorkOrderModalCancel={handleListItemWorkOrderModalCancel}
            workOrderNoForListItemModal={workOrderNoForListItemModal}
            existingItemCodesForListItemModal={existingItemCodesForListItemModal}
            warehouseForListItemModal={warehouseForListItemModal}
            handleListItemWorkOrderAdd={handleListItemWorkOrderAdd}
            autosaveStatus={displayAutosaveStatus}
            handleNew={handleNew}
            handleSubmit={handleSubmit}
            handleVoid={handleVoid}
            isVoidButtonDisabled={isVoidButtonDisabled}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
            isPrintButtonDisabled={isPrintButtonDisabled}
            handlePrintClick={handlePrintClick}
            isPrinting={isPrinting}
            isReportModalOpen={isReportModalOpen}
            handleReportModalCancel={handleReportModalCancel}
            lockedFields={lockedFields}
            onFieldFocus={onFieldFocus}
            onFieldBlur={onFieldBlur}
            onFormValuesChange={onFormValuesChange}
            getLockProps={getLockProps}
        />;
    };

    return ComponentWithLogic;
};

export default withSupplySlipLogic;